import { For, Match, ParentProps, Show, Switch, createContext, createEffect, createMemo, createResource, createSignal, onMount } from 'solid-js'
import { requireContext } from 'common'
import { supportAssist as api, CitationDocumentType, ResponseCitationReference, SupportResponse } from 'st-app/api'
import { SupportAssistContext } from './SupportAssistContext'
import styles from './st-support-assist.module.css'
import Textarea from '../st-textarea/st-textarea'
import Button from '../st-button/st-button'
import Image from '../st-image/st-image'
import Dropdown from '../st-dropdown/st-dropdown'
import STAILoader from '../st-ai-loader/st-ai-loader'
import { openViewWindow } from '../st-view/st-view'

const SupportAssistContextJsx = createContext<SupportAssistContext>();
export const requireSupportAssistContext = () => {
    return requireContext(SupportAssistContextJsx);
}

export class SupportAssistProps
{
    excludeSensitiveData?: boolean;
}

export default function SupportAssist(props: ParentProps<SupportAssistProps>) {
    const context = new SupportAssistContext();

    return (
        <SupportAssistContextJsx.Provider value={context}>
            <SupportAssistInternal {...props} />
        </SupportAssistContextJsx.Provider>
    )
}

function SupportAssistInternal(props: ParentProps<SupportAssistProps>) {

    const [prompt, setPrompt] = createSignal<string>('')
    const [response, setResponse] = createSignal<SupportResponse>()
    const [responseLoading, setResponseLoading] = createSignal(false)

    const submit = async () => {
        if (responseLoading()) return
        
        setResponseLoading(true)
        try {
            const res = await api.Prompt(prompt(), props.excludeSensitiveData ?? false) as SupportResponse
            setResponse(res)
        } catch {

        } finally {
            setResponseLoading(false)
        }
    }

    const reset = () => {
        setPrompt('')
        setResponse(undefined)
    }

    const onKeyUp = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            submit()
            e.preventDefault()
            e.stopPropagation()
        }
    }

    return (
        <div class={styles['st-support-assist']}>
            <div style={{ color: 'var(--st-hint-fg, var(--st-color-dim-fg))' }} class='flexbox horizontal middle'>
                <i class='fa  fa-brain-circuit' style='padding-right: 8px; font-size: 32px;'></i>
                Enter search criteria to identify documentation, ETNs and source resolved tickets to research
            </div>
            <Textarea disabled={responseLoading()} value={[prompt, setPrompt]} label='' rows={7} onKeyDown={onKeyUp} />
            <div style='display: flex; gap: 1em; width: fit-content; align-self: start;'>
                <Button icon='fa fa-brain-circuit' enabled={prompt().length > 0} type='primary' onClick={submit} loading={responseLoading()}>Submit</Button>
                <Button icon='fa fa-rotate-left' enabled={prompt().length > 0 && !responseLoading()} type='warning' onClick={reset}>Clear All</Button>
            </div>

            <div style='width: 100%; border-bottom: 1px solid var(--st-panel-border);'></div>

            <div class={styles['chat-result-container']} >
                <ResponseCard response={response()!} loading={responseLoading()} />
            </div>

        </div>
    );
}

function Loader() {
    return (
        <div class="flexbox vertical middle">
            <STAILoader processing={true} size='350px' />
        </div>
    )
}

function ResponseCard(props: { response: SupportResponse | undefined, loading: boolean }) {
    const refIndices = createMemo(() => {
        let i = 0
        return props.response?.citations?.map((citation) => {
            return citation.references?.map(() => { return i++; }) ?? []
        }) ?? []
    })

    const [showReferenceList, setShowReferenceList] = createSignal(true)
    const [submittingResponse, setSubmittingResponse] = createSignal(false)
    const [isHelpful, setIsHelpful] = createSignal<boolean | undefined>(undefined)

    createEffect(() => {
        const _ = props.response
        setIsHelpful(undefined)
    })

    const recordResponse = async (isHelpful: boolean) => {
        if (submittingResponse()) return
        try {
            await api.SetUserRating(props.response!.aiLogGU!, isHelpful)
            setIsHelpful(isHelpful)
        } catch (e) {
            console.error(e);
        } finally {
            setSubmittingResponse(false)
        }
    }

    const showReference = (citationIndex: number, refIndex: number) => {
        document.querySelector(`[data-ref-idx="${refIndices()[citationIndex][refIndex] + 1}"]`)?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <Show when={props.loading || props.response}>
            <div class={styles['chat-result-layout']}>
                <div>
                    <div class={styles['response-container']} style='min-height: 350px;'>
                        <div class={styles['response-left-col']}>
                            <div class={styles['response-avatar']}>
                                <Image
                                    classList={{
                                        'fa fa-spin': props.loading
                                    }}
                                    alt="Synergy Logo"
                                    src='Images/ST/synergy_logo_hires.png'
                                    width={'28px'}
                                    height={'28px'}
                                ></Image>
                            </div>

                            <Show when={!props.loading && props.response?.citations?.length}>
                                <i
                                    onClick={() => setShowReferenceList(!showReferenceList())}
                                    class={styles['reference-toggle']}
                                    classList={{
                                        'fa fa-eye': !showReferenceList(),
                                        'fa fa-eye-slash': showReferenceList()
                                    }}
                                />
                            </Show>
                        </div>
                        <div class={styles['response-card']}>
                            <Show when={!props.loading} fallback={<Loader />}>
                                <Switch>
                                    <Match when={props.response!.citations?.length > 0}>
                                        <For each={props.response!.citations}>{(citation, citationIndex) => (
                                            <>
                                                <span style='white-space: pre-wrap;'>
                                                    {citation.text}
                                                </span>
                                                <For each={citation.references}>{(reference, refIndex) => (
                                                    <Dropdown>
                                                        <Dropdown.Trigger
                                                            action='hover'
                                                            as='span'
                                                            class={styles['response-reference']}
                                                            onClick={(e) => { showReference(citationIndex(), refIndex()); return true; }}
                                                        >
                                                            [{refIndices()[citationIndex()][refIndex()] + 1}]
                                                        </Dropdown.Trigger>
                                                        <Dropdown.Content class={styles['response-reference-content']} as={'div'}>
                                                            <ReferenceCard reference={reference} />
                                                        </Dropdown.Content>
                                                    </Dropdown>
                                                )}</For>
                                            </>
                                        )}</For>
                                    </Match>
                                </Switch>
                            </Show>
                        </div>
                    </div>
                    <Show when={!props.loading}>
                        <div class={styles['response-card-actions']}>
                            <Button
                                icon='fa fa-thumbs-down'
                                type={isHelpful() === false ? 'danger' : 'default'}
                                onClick={() => recordResponse(false)}
                                loading={submittingResponse()}
                            />

                            <Button
                                icon='fa fa-thumbs-up'
                                type={isHelpful() === true ? 'success' : 'default'}
                                onClick={() => recordResponse(true)}
                                loading={submittingResponse()}
                            />
                        </div>
                    </Show>
                </div>

                <div style='max-height: 100%; overflow-y: auto;'>
                    <Show when={!props.loading && showReferenceList()}>
                        <For each={props.response?.citations}>{(citation, index) => (
                            <For each={citation.references}>{(reference, refIndex) => (
                                <div class={styles['response-container']}>
                                    <div class={styles['response-reference']} data-ref-idx={refIndices()[index()][refIndex()] + 1}>
                                        {'[' + (refIndices()[index()][refIndex()] + 1) + ']'}
                                    </div>
                                    <div class={styles['response-card']}>
                                        <ReferenceCard reference={reference} />
                                    </div>
                                </div>
                            )}</For>
                        )}</For>
                    </Show>
                </div>

            </div>

        </Show>
    )
}

function ReferenceCard(props: { reference: ResponseCitationReference}) {
    const icon = createMemo(() => {
        if (props.reference.title?.startsWith('Ticket: ')) {
            return <Image alt="Tickets Icon" src='Images/ST_VIEW/TICKETS.svg' style='padding: 8px; height: 46px;'></Image>
        } else if (props.reference.title?.endsWith('pdf')) {
            return <i class='fa fa-file-pdf' style='padding: 8px; font-size: 32px;'></i>
        } else {
            return <i class='fa fa-file' style='padding: 8px; font-size: 32px;'></i>
        }
    })

    const openReference = async () => {
        if (props.reference.ticketGU) {
            openViewWindow('C30E8A12-CAFD-4A7B-8302-B9F3C54B355C', undefined, props.reference.ticketGU)
        } else if (props.reference.uri) {
            const presignedUrl = await api.GetPresignedUrl(props.reference.uri);
            if (presignedUrl) {
                window.open(presignedUrl, '_blank');
            }
        }
    }

    return (
        <div>
            <h3 class={`${styles['reference-title']} flexbox horizontal middle`} onClick={openReference}>
                {icon()}
                {props.reference.title}
            </h3>
            <span style={{ 'white-space': 'pre-wrap' }}>
                {props.reference.text}
            </span>
        </div>
    )
}

SupportAssist.defaultProps = new SupportAssistProps();